import React from "react";
import { Box, Chip, Divider, Grid2, Paper, useTheme } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import { formatDateWithoutTime } from "../../../../../../views/components/common/functions/formatDate";
import {
  DailyTasksDetailResult,
  DailyTasksSlaStatus,
  PO_WORK_STATE_LABELS,
  PurchaseOrderDrawerDto,
  PurchaseRequestDrawerDto,
  PoWorkState,
} from "../../../../../../models/Dashboard/DailyTasks/DailyTasks";
import {
  getModernPageColors,
  modernSectionPaperSx,
} from "../../../../../../styles/modernListingPageSx";
import { dailyTasksChipSx, getDailyTasksChipColors } from "./dailyTasksChips";
import { DailyTasksHint, DAILY_TASKS_HINTS } from "./DailyTasksHint";
import { IdLink, purchaseRequestDetailsPath } from "./DailyTasksMedia";
import { useDailyTasksFieldPermissions } from "./useDailyTasksFieldPermissions";
import {
  DailyTasksEmptyState,
  DailyTasksSectionHeader,
} from "./DailyTasksEmptyState";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import LocalMallOutlinedIcon from "@mui/icons-material/LocalMallOutlined";

interface PoDrawerBodyProps {
  detail: DailyTasksDetailResult;
}

const formatDate = (value: string | Date | null | undefined): string => {
  if (!value) return "-";
  return formatDateWithoutTime(value) || "-";
};

const formatHours = (hours: number | null | undefined): string => {
  if (hours == null || !Number.isFinite(Number(hours))) return "-";
  const value = Math.round(Number(hours));
  if (value < 24) return `${value}h`;
  const days = Math.floor(value / 24);
  const rem = value % 24;
  return rem ? `${days}d ${rem}h` : `${days}d`;
};

const poWorkLabel = (
  state: DailyTasksDetailResult["mockup_work_state"],
): string => {
  if (state && state in PO_WORK_STATE_LABELS) {
    return PO_WORK_STATE_LABELS[state as PoWorkState];
  }
  return state ? state.replace(/_/g, " ") : "Sent to vendor";
};

const slaTone = (
  theme: Theme,
  status: DailyTasksSlaStatus,
): { bg: string; fg: string; label: string } => {
  try {
    if (status === "bad") {
      return { ...getDailyTasksChipColors(theme, "bad"), label: "Overdue" };
    }
    if (status === "warn") {
      return { ...getDailyTasksChipColors(theme, "warn"), label: "Watch" };
    }
    if (status === "ok") {
      return { ...getDailyTasksChipColors(theme, "ok"), label: "On track" };
    }
    return { ...getDailyTasksChipColors(theme, "info"), label: "Pending" };
  } catch (error) {
    return { ...getDailyTasksChipColors(theme, "info"), label: "Pending" };
  }
};

const StatTile: React.FC<{
  eyebrow: string;
  value: string;
  hint?: string;
  info?: string;
  pill?: { label: string; bg: string; fg: string };
}> = ({ eyebrow, value, hint, info, pill }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  return (
    <Box sx={{ height: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <CustomTypography
          variant="caption"
          fontWeight={600}
          sx={{
            color: colors.greyTextColor,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          {eyebrow}
        </CustomTypography>
        {info ? <DailyTasksHint text={info} /> : null}
      </Box>
      <CustomTypography
        variant="heading5"
        fontWeight={600}
        sx={{
          color: colors.navyText,
          letterSpacing: "-0.02em",
          lineHeight: 1.1,
          mt: 0.5,
        }}
      >
        {value}
      </CustomTypography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 0.75,
          mt: 0.75,
        }}
      >
        {pill ? (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: 1,
              py: 0.25,
              borderRadius: 1,
              bgcolor: pill.bg,
              color: pill.fg,
            }}
          >
            <CustomTypography variant="caption" fontWeight={700} color={pill.fg}>
              {pill.label}
            </CustomTypography>
          </Box>
        ) : null}
        {hint ? (
          <CustomTypography variant="caption" sx={{ color: colors.greyTextColor }}>
            {hint}
          </CustomTypography>
        ) : null}
      </Box>
    </Box>
  );
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  return (
    <CustomTypography
      variant="caption"
      fontWeight={600}
      sx={{
        color: getModernPageColors(theme).subtitle,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        display: "block",
        mb: 1.25,
      }}
    >
      {children}
    </CustomTypography>
  );
};

const MetaField: React.FC<{
  label: string;
  value: string;
  hint?: string;
}> = ({ label, value, hint }) => {
  const theme = useTheme();
  const pageColors = getModernPageColors(theme);
  return (
    <Grid2 size={{ xs: 6, sm: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minHeight: 20 }}>
        <CustomTypography
          variant="caption"
          fontWeight={600}
          sx={{
            color: pageColors.subtitle,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          {label}
        </CustomTypography>
        {hint ? <DailyTasksHint text={hint} /> : null}
      </Box>
      <CustomTypography
        variant="body2"
        fontWeight={500}
        sx={{ color: pageColors.title, mt: 0.25 }}
      >
        {value}
      </CustomTypography>
    </Grid2>
  );
};

const PoLine: React.FC<{
  order: PurchaseOrderDrawerDto;
  orderId: number;
}> = ({ order, orderId }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const emailed = Boolean(order.email_sent || order.email_sent_date);
  const prHref =
    order.pr_id != null
      ? purchaseRequestDetailsPath(order.pr_id, orderId)
      : null;
  return (
    <Box
      sx={{
        mt: 1,
        p: 1.25,
        borderRadius: 1.5,
        bgcolor: colors.veryLightGray,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {prHref ? (
          <IdLink
            href={prHref}
            label={`PO ${order.po_number || order.po_id}`}
            fontSize="0.8rem"
          />
        ) : (
          <CustomTypography variant="body3" fontWeight={600}>
            PO {order.po_number || order.po_id}
          </CustomTypography>
        )}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          <Chip
            size="small"
            label={order.status || "Issued"}
            sx={dailyTasksChipSx(theme, "ok")}
          />
          <Chip
            size="small"
            label={
              emailed
                ? `Email sent ${formatDate(order.email_sent_date)}`
                : "Email not sent"
            }
            sx={dailyTasksChipSx(theme, emailed ? "ok" : "warn")}
          />
        </Box>
      </Box>
      <CustomTypography
        variant="caption"
        sx={{ color: colors.greyTextColor, display: "block", mt: 0.5 }}
      >
        {order.created_by
          ? `Created ${formatDate(order.created_date)} by ${order.created_by}`
          : `Created ${formatDate(order.created_date)}`}
        {order.vendor_name ? ` · ${order.vendor_name}` : ""}
        {order.warehouse_id ? ` · Warehouse ${order.warehouse_id}` : ""}
        {order.events?.expected_at
          ? ` · Expected ${formatDate(order.events.expected_at)}`
          : ""}
      </CustomTypography>
    </Box>
  );
};

const PrWithPos: React.FC<{
  request: PurchaseRequestDrawerDto;
  orders: PurchaseOrderDrawerDto[];
  orderedQuantity: number;
  orderId: number;
}> = ({ request, orders, orderedQuantity, orderId }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const cancelled = Boolean(request.pr_cancellation_date);
  const hasPo = orders.length > 0;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.75,
        border: `1px solid ${colors.softBorder}`,
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: 0.5,
        }}
      >
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            <IdLink
              href={purchaseRequestDetailsPath(request.id, orderId)}
              label={`PR #${request.id}`}
              fontSize="0.875rem"
            />
            <CustomTypography variant="body2" fontWeight={600}>
              · {request.vendor_name || "No vendor"}
            </CustomTypography>
          </Box>
          <CustomTypography
            variant="caption"
            sx={{ color: colors.greyTextColor, display: "block", mt: 0.25 }}
          >
            Qty {request.quantity}
            {orderedQuantity > 0 ? ` of ${orderedQuantity}` : ""}
            {request.created_by
              ? ` · Created ${formatDate(request.created_date)} by ${request.created_by}`
              : ` · Created ${formatDate(request.created_date)}`}
            {request.expected_delivery_date
              ? ` · Expected ${formatDate(request.expected_delivery_date)}`
              : ""}
          </CustomTypography>
        </Box>
        <Chip
          size="small"
          label={
            cancelled
              ? "Cancelled"
              : !request.is_clean
                ? "Rejected"
                : hasPo
                  ? orders.some(
                      (order) => order.email_sent || order.email_sent_date,
                    )
                    ? "Emailed"
                    : "Awaiting email"
                  : "Needs PO"
          }
          sx={dailyTasksChipSx(
            theme,
            cancelled || !request.is_clean
              ? "bad"
              : hasPo
                ? orders.some(
                    (order) => order.email_sent || order.email_sent_date,
                  )
                  ? "ok"
                  : "warn"
                : "warn",
          )}
        />
      </Box>
      {hasPo ? (
        orders.map((order) => (
          <PoLine key={order.po_id} order={order} orderId={orderId} />
        ))
      ) : (
        <DailyTasksEmptyState
          inPaper={false}
          icon={LocalMallOutlinedIcon}
          title="No purchase order yet"
          description="A PO has not been issued against this purchase request."
          sx={{ mt: 1 }}
        />
      )}
    </Paper>
  );
};

const PoDrawerBody: React.FC<PoDrawerBodyProps> = ({ detail }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const { canViewDispatchDate } = useDailyTasksFieldPermissions();
  const sla = slaTone(theme, detail.sla_status);
  const daysHint =
    detail.days_to_sla == null
      ? null
      : detail.days_to_sla < 0
        ? `${Math.abs(detail.days_to_sla)} day${
            Math.abs(detail.days_to_sla) === 1 ? "" : "s"
          } past SLA`
        : detail.days_to_sla === 0
          ? "Due today"
          : `${detail.days_to_sla} day${detail.days_to_sla === 1 ? "" : "s"} left`;
  const clockHint =
    detail.mockup_work_state === "email_to_vendor"
      ? "Since oldest issued PO still waiting for vendor email"
      : "Since oldest approved PR still waiting for a PO";
  const requests = detail.purchase_requests ?? [];
  const orders = detail.purchase_orders ?? [];
  const approvedPrCount = detail.approved_pr_count ?? 0;
  const issuedPoCount = detail.issued_po_count ?? 0;
  const sentPoCount = detail.sent_po_count ?? 0;
  const qtyPct =
    approvedPrCount > 0
      ? Math.min(100, Math.round((issuedPoCount / approvedPrCount) * 100))
      : 0;
  const emailPct =
    issuedPoCount > 0
      ? Math.min(100, Math.round((sentPoCount / issuedPoCount) * 100))
      : 0;
  const remaining = Math.max(approvedPrCount - issuedPoCount, 0);
  const remainingEmail = Math.max(issuedPoCount - sentPoCount, 0);
  const ordersByPr = new Map<number, PurchaseOrderDrawerDto[]>();
  for (const order of orders) {
    if (order.pr_id == null) continue;
    const list = ordersByPr.get(order.pr_id) ?? [];
    list.push(order);
    ordersByPr.set(order.pr_id, list);
  }

  return (
    <>
      <Paper elevation={0} sx={modernSectionPaperSx(theme)}>
        <Grid2 container spacing={2} columns={12}>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <StatTile
              eyebrow="Elapsed"
              value={formatHours(detail.sla_elapsed_hours)}
              hint={`Since ${formatDate(detail.sla_clock_start)}`}
              info={DAILY_TASKS_HINTS.drawerPoElapsed}
              pill={{ label: sla.label, bg: sla.bg, fg: sla.fg }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <StatTile
              eyebrow="PO SLA"
              value={formatHours(detail.sla_target_hours)}
              hint={daysHint ?? clockHint}
              info={DAILY_TASKS_HINTS.drawerPoSla}
            />
          </Grid2>
        </Grid2>
        <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
          <Chip
            size="small"
            label={poWorkLabel(detail.mockup_work_state)}
            sx={dailyTasksChipSx(
              theme,
              detail.mockup_work_state === "needs_po"
                ? "bad"
                : detail.mockup_work_state === "email_to_vendor"
                  ? "warn"
                  : detail.mockup_work_state === "partial_po"
                    ? "info"
                    : "ok",
            )}
          />
          <CustomTypography
            variant="caption"
            sx={{ color: colors.greyTextColor, alignSelf: "center" }}
          >
            {clockHint}
          </CustomTypography>
        </Box>
        <Box sx={{ mt: 1.75 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              mb: 0.75,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <CustomTypography variant="caption" fontWeight={600} sx={{ color: colors.greyTextColor }}>
                Issued POs
              </CustomTypography>
              <DailyTasksHint text={DAILY_TASKS_HINTS.drawerPoIssued} />
            </Box>
            <CustomTypography variant="caption" fontWeight={600} sx={{ color: colors.navyText }}>
              {issuedPoCount} / {approvedPrCount}
              {remaining > 0 ? ` · ${remaining} PR${remaining === 1 ? "" : "s"} still need a PO` : ""}
            </CustomTypography>
          </Box>
          <Box
            sx={{
              height: 6,
              borderRadius: 99,
              bgcolor: colors.veryLightGray,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${qtyPct}%`,
                height: "100%",
                bgcolor:
                  remaining === 0 ? colors.successGreen : colors.darkBlue,
              }}
            />
          </Box>
        </Box>
        <Box sx={{ mt: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              mb: 0.75,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <CustomTypography variant="caption" fontWeight={600} sx={{ color: colors.greyTextColor }}>
                Emailed to vendor
              </CustomTypography>
              <DailyTasksHint text={DAILY_TASKS_HINTS.drawerPoEmailed} />
            </Box>
            <CustomTypography variant="caption" fontWeight={600} sx={{ color: colors.navyText }}>
              {sentPoCount} / {issuedPoCount}
              {remainingEmail > 0
                ? ` · ${remainingEmail} PO${remainingEmail === 1 ? "" : "s"} still need email`
                : issuedPoCount === 0
                  ? " · Issue a PO first"
                  : ""}
            </CustomTypography>
          </Box>
          <Box
            sx={{
              height: 6,
              borderRadius: 99,
              bgcolor: colors.veryLightGray,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${emailPct}%`,
                height: "100%",
                bgcolor:
                  remainingEmail === 0 && issuedPoCount > 0
                    ? colors.successGreen
                    : colors.darkBlue,
              }}
            />
          </Box>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ ...modernSectionPaperSx(theme), mt: 2 }}>
        <SectionLabel>Order</SectionLabel>
        <Grid2 container spacing={1.5} columns={12}>
          <MetaField label="Type" value={detail.order_type || "-"} />
          <MetaField label="Qty" value={String(detail.ordered_quantity ?? 0)} />
          <MetaField label="PM" value={detail.pm_poc || "Unassigned"} />
          <MetaField label="Sales" value={detail.sales_poc || "-"} />
          {canViewDispatchDate && detail.dispatch_date ? (
            <MetaField label="Dispatch" value={formatDate(detail.dispatch_date)} />
          ) : null}
        </Grid2>
        <Divider sx={{ my: 2 }} />
        <SectionLabel>PO clock</SectionLabel>
        <Grid2 container spacing={1.5} columns={12}>
          <MetaField
            label="First PO"
            value={formatDate(detail.first_po_at)}
            hint={DAILY_TASKS_HINTS.drawerPoFirst}
          />
          <MetaField
            label="All POs"
            value={formatDate(detail.all_po_at)}
            hint={DAILY_TASKS_HINTS.drawerPoAllIssued}
          />
          <MetaField
            label="All emailed"
            value={formatDate(detail.all_email_at)}
            hint={DAILY_TASKS_HINTS.drawerPoAllEmailed}
          />
          <MetaField
            label="PO TAT"
            value={formatHours(detail.po_creation_tat_hours)}
            hint={DAILY_TASKS_HINTS.drawerPoTat}
          />
          <MetaField
            label="Total TAT"
            value={formatHours(detail.total_tat_hours)}
            hint={DAILY_TASKS_HINTS.drawerTotalTat}
          />
        </Grid2>
      </Paper>

      <Box sx={{ mt: 3, mb: 2 }}>
        <DailyTasksSectionHeader
          title="Purchase requests"
          count={requests.length}
        />
        {requests.length === 0 ? (
          <DailyTasksEmptyState
            icon={AssignmentOutlinedIcon}
            title="No purchase requests yet"
            description="Approved PRs for this product will show here with linked purchase orders."
            sx={{ mt: 0 }}
          />
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {requests.map((request) => (
              <PrWithPos
                key={request.id}
                request={request}
                orders={ordersByPr.get(request.id) ?? []}
                orderedQuantity={detail.ordered_quantity ?? 0}
                orderId={detail.order_id}
              />
            ))}
          </Box>
        )}
      </Box>
    </>
  );
};

export default PoDrawerBody;
